import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface BetaCode {
  id: string;
  org_id: string;
  code: string;
  label: string;
  max_uses: number | null;
  current_uses: number;
  is_active: boolean;
  roles: string[];
  expires_at: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

interface CreateBetaCodeData {
  code: string;
  label: string;
  max_uses?: number | null;
  expires_at?: string | null;
  roles?: string[];
}

export function useBetaCodes() {
  return useQuery({
    queryKey: ["beta-codes"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("beta_access_code")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as BetaCode[];
    },
  });
}

export function useCreateBetaCode() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (newCode: CreateBetaCodeData) => {
      // Get current user's org_id
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error("Not authenticated");

      const { data: userAccount } = await supabase
        .from("user_account")
        .select("id, org_id")
        .eq("external_auth_user", userData.user.id)
        .single();

      if (!userAccount) throw new Error("User account not found");

      const { data, error } = await supabase
        .from("beta_access_code")
        .insert({
          ...newCode,
          code: newCode.code.toUpperCase().trim(),
          org_id: userAccount.org_id,
          created_by: userAccount.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["beta-codes"] });
      toast({
        title: "Beta code created",
        description: "The beta access code has been created successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to create code",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

export function useUpdateBetaCode() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<BetaCode> }) => {
      const { data, error } = await supabase
        .from("beta_access_code")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["beta-codes"] });
      toast({
        title: "Code updated",
        description: "The beta code has been updated successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to update code",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

export function useDeactivateBetaCode() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("beta_access_code")
        .update({ is_active: false })
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["beta-codes"] });
      toast({
        title: "Code deactivated",
        description: "The beta code has been deactivated.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to deactivate code",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

export function useDeleteBetaCode() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("beta_access_code")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["beta-codes"] });
      toast({
        title: "Code deleted",
        description: "The beta code has been deleted.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to delete code",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

export function useBetaCodeStats() {
  return useQuery({
    queryKey: ["beta-code-stats"],
    queryFn: async () => {
      const { data: codes, error: codesError } = await supabase
        .from("beta_access_code")
        .select("id, current_uses, is_active");

      if (codesError) throw codesError;

      const { data: signups, error: signupsError } = await supabase
        .from("beta_signup")
        .select("id");

      if (signupsError) throw signupsError;

      const activeCodes = codes?.filter(c => c.is_active).length || 0;
      const totalSignups = signups?.length || 0;
      
      const mostUsedCode = codes?.reduce((max, code) => 
        code.current_uses > (max?.current_uses || 0) ? code : max
      , codes[0]);

      return {
        totalActiveCodes: activeCodes,
        totalSignups,
        mostUsedCodeUses: mostUsedCode?.current_uses || 0,
      };
    },
  });
}
